/**
 * Workspace pages tab — the archived pages of the workspace (FRIDAY-69).
 *
 * The sidebar page tree only shows active pages. Archived pages are listed
 * here: owners and admins can restore them, and the owner can delete one
 * permanently with its whole subtree (the same gates as the server). Archiving a page archives its
 * sub-pages with it, so the list shows that page once, with a count of the
 * sub-pages that come back when it is restored.
 */

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArchiveRestore, Trash2 } from 'lucide-react'
import type { Page } from '@friday/shared/realtime'
import { ApiError } from '@/lib/api'
import { usePageTree, useRestorePage, useDeletePageHard } from '@/features/notebook/pages'
import { useWorkspaces } from '@/features/workspaces/workspaces'
import { ConfirmDialog } from '@/components/feedback'
import { toast } from '@/features/feedback/toast'

interface ArchivedRow {
  page: Page
  /** Titles of the page's ancestors, root first. */
  path: string[]
  /** The parent is archived on its own, so this page cannot be restored yet. */
  parentArchived: boolean
  /** Sub-pages archived together with this page; they come back with it. */
  descendants: number
  /** Every page below this one; a permanent delete removes them all. */
  subtree: number
}

/**
 * One row per archive operation. Archiving a page archives its sub-pages with
 * the same timestamp, so those sub-pages are counted on their page's row
 * instead of being listed again. A sub-page archived on its own, at another
 * time, gets its own row.
 */
function archivedRows(pages: Page[]): ArchivedRow[] {
  const byId = new Map(pages.map((p) => [p.id, p]))
  const children = new Map<string, Page[]>()
  for (const p of pages) {
    if (!p.parentPageId) continue
    children.set(p.parentPageId, [...(children.get(p.parentPageId) ?? []), p])
  }
  const archivedWithParent = (p: Page) => {
    const parent = p.parentPageId ? byId.get(p.parentPageId) : undefined
    return !!parent?.archivedAt && parent.archivedAt === p.archivedAt
  }
  const countArchivedWith = (page: Page): number =>
    (children.get(page.id) ?? [])
      .filter((child) => child.archivedAt === page.archivedAt)
      .reduce((n, child) => n + 1 + countArchivedWith(child), 0)
  const countSubtree = (page: Page): number =>
    (children.get(page.id) ?? []).reduce((n, child) => n + 1 + countSubtree(child), 0)

  return pages
    .filter((p) => p.archivedAt && !archivedWithParent(p))
    .map((page) => {
      const path: string[] = []
      let cur = page.parentPageId ? byId.get(page.parentPageId) : undefined
      while (cur) {
        path.unshift(cur.title)
        cur = cur.parentPageId ? byId.get(cur.parentPageId) : undefined
      }
      const parent = page.parentPageId ? byId.get(page.parentPageId) : undefined
      return {
        page,
        path,
        parentArchived: !!parent?.archivedAt,
        descendants: countArchivedWith(page),
        subtree: countSubtree(page),
      }
    })
    .sort((a, b) => (b.page.archivedAt ?? '').localeCompare(a.page.archivedAt ?? ''))
}

export function WorkspacePagesPanel({ wsId }: { wsId: string }) {
  const tree = usePageTree(wsId, { includeArchived: true })
  const restore = useRestorePage(wsId)
  const del = useDeletePageHard(wsId)
  const { data: workspaces } = useWorkspaces()
  const role = workspaces?.find((w) => w.id === wsId)?.role
  // The server's gates: page writes need owner or admin, hard delete owner.
  const canWrite = role === 'owner' || role === 'admin'
  const canDelete = role === 'owner'
  const [pendingDelete, setPendingDelete] = useState<ArchivedRow | null>(null)

  const rows = useMemo(() => archivedRows(tree.data ?? []), [tree.data])

  if (tree.isLoading) {
    return (
      <p className="text-surface-500 p-4 text-sm" role="status">
        Loading pages…
      </p>
    )
  }

  if (tree.isError) {
    return (
      <div className="p-4" role="alert">
        <p className="text-sm text-red-600 dark:text-red-300">Could not load the pages of this workspace.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => void tree.refetch()}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <section className="space-y-3" aria-labelledby="archived-pages-heading">
      <h2 id="archived-pages-heading" className="text-sm font-semibold">
        Archived pages
      </h2>
      {rows.length === 0 ? (
        <div className="border-surface-200 text-surface-500 dark:border-surface-700 rounded-lg border border-dashed p-12 text-center text-sm">
          No archived pages. Archive a page from its menu in the sidebar.
        </div>
      ) : (
        <ul className="space-y-2" aria-label="Archived pages">
          {rows.map((row) => {
            const { page, path, parentArchived, descendants } = row
            return (
              <li key={page.id} className="card p-3" data-testid="archived-page">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-lg" aria-hidden="true">
                    {page.icon ?? '📄'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/w/$wsId/n/$pageId"
                      params={{ wsId, pageId: page.id }}
                      className="block truncate text-sm font-semibold hover:underline"
                    >
                      {page.title}
                    </Link>
                    <div className="text-surface-500 text-xs">
                      {path.length > 0 ? `In ${path.join(' / ')}` : 'Top-level page'}
                      {descendants > 0 && ` · ${descendants} sub-page${descendants === 1 ? '' : 's'}`}
                      {page.archivedAt && ` · archived ${new Date(page.archivedAt).toLocaleDateString()}`}
                    </div>
                    {parentArchived && (
                      <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                        Its parent page is archived. Restore the parent first.
                      </div>
                    )}
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      className="btn-ghost min-h-11 text-xs"
                      disabled={parentArchived || (restore.isPending && restore.variables === page.id)}
                      onClick={() =>
                        restore.mutate(page.id, {
                          onSuccess: () => toast(`Restored "${page.title}"`, { kind: 'success' }),
                          onError: (e) =>
                            toast(e instanceof ApiError ? e.message : 'Could not restore the page', {
                              kind: 'error',
                            }),
                        })
                      }
                    >
                      <ArchiveRestore size={14} aria-hidden="true" />
                      Restore
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="btn-ghost min-h-11 text-xs text-red-700 dark:text-red-300"
                      onClick={() => setPendingDelete(row)}
                      aria-label={`Delete ${page.title} permanently`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                      Delete
                    </button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {!canWrite && rows.length > 0 && (
        <p className="text-surface-500 text-xs">
          Only workspace owners and admins can restore archived pages.
        </p>
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete "${pendingDelete?.page.title ?? ''}" permanently?`}
        body={
          <p className="text-surface-600 dark:text-surface-300 text-sm">
            The page, its revisions and its task links are removed. This cannot be undone.
            {pendingDelete &&
              pendingDelete.subtree > 0 &&
              ` Its ${pendingDelete.subtree} sub-page${pendingDelete.subtree === 1 ? ' is' : 's are'} deleted with it.`}
          </p>
        }
        confirmLabel="Delete page"
        destructive
        busy={del.isPending}
        onClose={() => !del.isPending && setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return
          const target = pendingDelete.page
          del.mutate(target.id, {
            onSuccess: () => {
              setPendingDelete(null)
              toast(`Deleted "${target.title}"`, { kind: 'success' })
            },
            onError: (e) =>
              toast(e instanceof ApiError ? e.message : 'Could not delete the page', { kind: 'error' }),
          })
        }}
      />
    </section>
  )
}
